import React, { useEffect, useMemo, useRef, useState } from 'react'
import Checkbox from 'components/Checkbox/checkbox'
import * as S from './ExportModal.styles'
import { hasIntersection } from 'utils/hasIntersection'
import { Modal } from '../../UI/Modal/Modal'
import ReactSelectWrapper from 'components/FormUtils/ReactSelectWrapper'
import { useColumnStoreContext } from 'stores/ColumnsStore/useColumnsStoreContext'
import apiService from 'services/api'
import { TableTypeSlug } from 'models/companies'
import { Column } from 'stores/ColumnsStore/ColumnsStore'

export interface ExportModalColumn {
  title: string
  elements: string[][]
}

export interface ExportModalProps {
  show: boolean
  _handleClose: () => void
  count: number
  filters: Record<string, unknown>
  productSelected?: number
  selectedIds?: number[]
  excludeIds?: number[]
  tableTypeSlug: TableTypeSlug
  endpointArgs?: string[]
}

interface ExportModalPropsPrivate extends ExportModalProps {
  columns: ExportModalColumn[]
  title: string
  subTitle: string
  submitText: string
  overrideDataTypeName?: string
  endpointArgs?: string[]
  /** Column IDs that should always be selected by default, even if not in selectedColumns */
  alwaysSelectedColumns?: string[]
}

export default function ExportModal(props: ExportModalPropsPrivate) {
  const selectedColumns = useColumnStoreContext(
    (state) => state.selectedColumns
  )

  const controller = useRef<AbortController>(new AbortController())
  const api = apiService()

  const onSubmit = async (
    selectedColumns: string[],
    exportType: 'csv' | 'xlsx'
  ) => {
    await api.commonExportTable(
      props.tableTypeSlug,
      {
        include_ids: props.selectedIds ?? [],
        exclude_ids: props.excludeIds ?? [],
        filter_params: { ...props.filters, product_id: props.productSelected },
        selected_columns: selectedColumns,
        export_type: exportType,
      },
      controller.current,
      props.overrideDataTypeName,
      props.endpointArgs
    )
  }

  useEffect(() => {
    return () => {
      controller.current.abort()
    }
  }, [])

  return (
    <BaseExportModal
      {...props}
      selectedColumns={selectedColumns}
      onSubmit={onSubmit}
      handleClose={props._handleClose}
    />
  )
}

export interface BaseExportModalProps {
  selectedColumns?: Column[]
  columns: ExportModalColumn[]
  onSubmit: (
    selectedColumns: string[],
    exportType: 'csv' | 'xlsx'
  ) => Promise<void>
  show: boolean
  handleClose: () => void
  title: string
  subTitle: string
  submitText: string
  disableExportTypes?: boolean
  /** Column IDs that should always be selected by default, even if not in selectedColumns */
  alwaysSelectedColumns?: string[]
}

export function BaseExportModal(props: BaseExportModalProps) {
  const [exportType, setExportType] = useState<'csv' | 'xlsx'>('csv')

  const [uncheckedList, setUncheckedList] = useState<string[]>([])

  useEffect(() => {
    if (!props.selectedColumns) return

    const columnsToUncheck: string[] = []
    props.columns.forEach((column) => {
      column.elements.forEach((element) => {
        const isAlwaysSelected = props.alwaysSelectedColumns?.includes(
          element[0]
        )
        if (
          !isAlwaysSelected &&
          !props.selectedColumns?.find((col) => col.id === element[0])
        ) {
          columnsToUncheck.push(element[0])
        }
      })
    })

    setUncheckedList(columnsToUncheck)
  }, [
    props.selectedColumns,
    props.columns,
    props.alwaysSelectedColumns,
    setUncheckedList,
  ])

  const manageUnchecked = (id: string) => {
    if (uncheckedList.includes(id)) {
      setUncheckedList((prevArray) => prevArray.filter((elem) => elem !== id))
    } else {
      setUncheckedList((prevArray) => [...prevArray, id])
    }
  }

  async function onSubmit() {
    const selected: string[] = []
    props.columns.forEach((column) => {
      column.elements.forEach((element) => {
        if (!uncheckedList.includes(element[0])) {
          selected.push(element[0])
        }
      })
    })

    await props.onSubmit(selected, exportType)

    if (props.show) props.handleClose()
  }

  return (
    <Modal
      size={'lg'}
      title={props.title}
      description={props.subTitle}
      open={props.show}
      onOpenChange={(open) => !open && props.handleClose()}
      onAccept={onSubmit}
      acceptButtonText={`${props.submitText}`}
      footerChildren={
        !props.disableExportTypes && (
          <SelectExportType
            exportType={exportType}
            setExportType={setExportType}
          />
        )
      }
    >
      <div className={'flex gap-3'}>
        {props.columns?.map((el) => (
          <div className={'flex-1'} key={el.title}>
            <div className={'flex items-center gap-2'}>
              <Checkbox
                boxSize={'lg'}
                checked={
                  !hasIntersection(
                    el.elements.map((it) => it[0]),
                    uncheckedList
                  )
                }
                onChange={() => {
                  if (
                    hasIntersection(
                      el.elements.map((it) => it[0]),
                      uncheckedList
                    )
                  ) {
                    setUncheckedList((prevArray) =>
                      prevArray.filter(
                        (elem) => !el.elements.map((el) => el[0]).includes(elem)
                      )
                    )
                  } else {
                    setUncheckedList((prevArray) => [
                      ...prevArray,
                      ...el.elements.map((el) => el[0]),
                    ])
                  }
                }}
              />
              <S.ColumnLabel>{el.title}</S.ColumnLabel>
            </div>
            {el.elements.map((item) => {
              return (
                <ColumnItem
                  key={item[0]}
                  label={item[1]}
                  checked={!uncheckedList.includes(item[0])}
                  onChange={() => manageUnchecked(item[0])}
                />
              )
            })}
          </div>
        ))}
      </div>
    </Modal>
  )
}

function SelectExportType({
  exportType,
  setExportType,
}: {
  exportType: 'csv' | 'xlsx'
  setExportType: (type: 'csv' | 'xlsx') => void
}) {
  const _value = useMemo(() => {
    return { label: exportType.toUpperCase(), value: exportType }
  }, [exportType])

  return (
    <div className={'flex gap-3 justify-center'}>
      <div className="w-1/3">
        <label
          className="text-base font-medium text-gray-800 select-none mb-1"
          htmlFor="exportType"
        >
          Export Type
        </label>
        <ReactSelectWrapper
          name={'exportType'}
          options={[
            { label: 'CSV', value: 'csv' },
            { label: 'XLSX', value: 'xlsx' },
          ]}
          value={_value}
          onChange={(value) =>
            setExportType((value as { value: 'csv' | 'xlsx' }).value)
          }
        />
      </div>
    </div>
  )
}

function ColumnItem(props: {
  label: string
  onChange: (checked: boolean) => void
  checked?: boolean
}) {
  const checkBoxRef = useRef<HTMLInputElement>(null)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    props.onChange(e.target.checked)
  }

  const toggleChecked = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault()
    e.stopPropagation()
    checkBoxRef.current?.click()
  }

  return (
    <S.ColumnItemContainer checked={props.checked} onClick={toggleChecked}>
      <Checkbox
        ref={checkBoxRef}
        checked={props.checked}
        onChange={handleChange}
      />
      {props.label}
    </S.ColumnItemContainer>
  )
}
